import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { FirebaseError } from 'firebase/app';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { navigateMypage } from '../../api/firebase/auth/index';
import { loginSchema } from '../../api/firebase/auth/validation';
import { auth } from '../../api/firebase/config';
import {
  blackText,
  linkColor,
  primaryButtonBackground,
  primarycolor
} from '../../constants/Colors';
import {
  cardContentPadding,
  cardSize,
  textMb,
  titleMb,
  w100
} from '../../constants/Location';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import CustomTextfield from './CustomTextfield';
import PasswordTextFiled from './PasswordTextField';

interface FormInputs {
  email: string;
  password: string;
}

const styles = {
  CheckboxM: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    marginLeft: '36px'
  },
  cardContentPadding,
  cardSize,
  required: {
    color: primarycolor,
    marginLeft: '10px'
  },
  textMb,
  textSmall: {
    fontSize: '11.5px'
  },
  titleMb,
  w100
};

const LoginCard: React.FC = () => {
  const { t } = useTranslation();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<FormInputs>({
    resolver: yupResolver(loginSchema)
  });

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [firstSubmit, setFirstSubmit] = useState(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('email') === undefined ||
        '' ||
        watch('password') === undefined ||
        '' ||
        Boolean(errors.email) ||
        Boolean(errors.password)
      );
    }
    return false;
  };

  const submit = async (): Promise<void> => {
    try {
      setLoading(true);
      const userAuth = await signInWithEmailAndPassword(
        auth,
        watch('email'),
        watch('password')
      );
      if (userAuth) {
        await navigateMypage(userAuth.user, navigate);
      } else {
        throw new Error();
      }
    } catch (error: unknown) {
      setOpen(true);
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/user-not-found':
            setErrorText(t('validation.error.emailOrPasswordWrong'));
            break;
          case 'auth/wrong-password':
            setErrorText(t('validation.error.emailOrPasswordWrong'));
            break;
          default:
            setErrorText(t('validation.error.loginDefault'));
            break;
        }
      } else {
        setErrorText(t('validation.error.loginDefault'));
      }
      throwError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    currentUser && navigateMypage(currentUser, navigate);
  }, [currentUser]);

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={12}>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: blackText }]}
                component="div"
              >
                {t('header.signin')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    labelText={t('form.email')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder="xxx@en-broker.co.jp"
                  />
                )}
                control={control}
                name="email"
              />
              <Controller
                render={({ field }) => (
                  <PasswordTextFiled
                    {...field}
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    labelText={t('form.password')}
                    requiredText={t('form.required')}
                    isRequired
                    id="password"
                    placeholder=" "
                    fullWidth
                  />
                )}
                control={control}
                name="password"
              />
            </CardContent>
            <CardActions sx={[styles.textMb]}>
              <Grid container direction="column" alignItems="center">
                <Button
                  disabled={buttonDisabledFlag()}
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    void (async () => {
                      setFirstSubmit(true);
                      await handleSubmit(async () => await submit())();
                    })();
                  }}
                  sx={[
                    primaryButtonBackground,
                    {
                      '@media screen and (max-width:960px)': { height: '42px' }
                    }
                  ]}
                >
                  <Typography
                    component="div"
                    color="white"
                    sx={{ fontSize: 15 }}
                  >
                    {t('header.signin')}
                  </Typography>
                </Button>
              </Grid>
            </CardActions>
            <CardContent sx={{ fontSize: '14px' }}>
              <Typography sx={[textMb, { marginLeft: '24px' }]}>
                <Link sx={linkColor} href={`/main/auth/password_reset`}>
                  {t('common.forget')}
                  {t('common.here')}
                </Link>
              </Typography>
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={errorText}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default LoginCard;
